import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useOutletContext, useParams } from "react-router-dom"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import _ from "lodash"
import { lang } from "config"
import Input from "components/forms/input"
import ErrorMessage from "components/forms/error-message"
import SelectSearchable from "components/forms/select-searchable"
import { useAuth } from "contexts/auth-context"
import { useResultSet } from "repositories/db-repository"
import { getBranchList, getGenericSupplier, getParameter } from "helpers/general-helper"
import { Plus } from "components/icons/outline"
import PurchaseExportActions from "./export-actions"

const PO_TYPE = "REGULAR"

function todayISO() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function moneyFmt(v) {
  const n = num(v)
  if (!n) return "-"
  return Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR" }).format(n)
}

function pickCostPrice(p) {
  const candidates = [
    p?.price,
    p?.pcs_price,
    p?.maximum_retail_price,
    p?.purchase_price,
    p?.buy_price,
    p?.cost_price,
    p?.hpp,
    p?.cogs,
  ]
  for (const c of candidates) {
    const n = Number(c)
    if (Number.isFinite(n) && n > 0) return n
  }
  return 0
}

function pickUom(p) {
  return p?.uom
}

function pickProductLabel(p) {
  return p?.product_unit_long_name || p?.product_unit || p?.sku || p?.product_model || ""
}

function normalizeDateInput(value) {
  if (!value) return ""
  if (typeof value !== "string") return ""
  return value.slice(0, 10)
}

export default function PurchaseOrderForm({ data, errors: externalErrors, onSubmit }) {
  const { id } = useParams()
  const isEdit = Boolean(id)

  const { can, currentBranch,user } = useAuth()
  const { title, transition, setTransition } = useOutletContext()
  const location = useLocation()

  const branchSelection = getBranchList()
  const supplierSelection = getGenericSupplier()

  const [errors, setErrors] = useState(externalErrors || {})

  // Header state
  const [poId, setPoId] = useState(null)
  const [poNumber, setPoNumber] = useState(null)
  const [outletId, setOutletId] = useState(currentBranch?.id ?? null)
  const [outletName, setOutletName] = useState(currentBranch?.name ?? null)
  const [supplierId, setSupplierId] = useState(null)
  const [supplierName, setSupplierName] = useState(null)
  const [poDate, setPoDate] = useState(todayISO())
  const [notes, setNotes] = useState("")
  const [status, setStatus] = useState("DRAFT")

  const outletGroupId = useMemo(() => getParameter("OUTLET_GROUP_ID") ?? null, [])

  // Items state
  const [items, setItems] = useState([])

  // UI add item
  const [selectedProductUnitId, setSelectedProductUnitId] = useState("")
  const [addQty, setAddQty] = useState(1)
  const [productSelectKey, setProductSelectKey] = useState(0)

  // SERVER SEARCH STATE
  const [productSearch, setProductSearch] = useState("") // debounced (triggers fetch)
  const [productSearchValue, setProductSearchValue] = useState("") // instant input value
  const [productPage, setProductPage] = useState(1)
  const perPage = 20

  const [selectedProductUnit, setSelectedProductUnit] = useState(null)

  const productCacheRef = useRef(new Map())

  // keep errors in sync from parent
  useEffect(() => {
    setErrors(externalErrors || {})
  }, [externalErrors])

  useEffect(() => {
    if (transition?.reverse) setTransition({ type: "slide", reverse: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ✅ Hydrate from props.data (NO FETCH HERE)
  useEffect(() => {
    if (!data) return

    // fleksibel: kalau loader bungkus di purchase_transaction
    const po = data?.purchase_transaction ?? data?.data ?? data

    setPoId(po?.id ?? null)
    setPoNumber(po?.po_number ?? po?.public_id ?? null)

    const outId = po?.outlet_id ?? currentBranch?.id ?? null
    setOutletId(outId)

    const supId = po?.supplier_id ?? null
    setSupplierId(supId)
    console.log(po,"po_date")
    setPoDate(normalizeDateInput(po?.po_date) || todayISO())
    setNotes(po?.remarks ?? po?.notes ?? "")
    setStatus(po?.status ?? po?.latest_flow_detail_id ?? "DRAFT")

    const b = branchSelection?.find((x) => String(x.id) === String(outId))
    setOutletName(b?.name ?? po?.outlet_name ?? null)

    const s = supplierSelection?.find((x) => String(x.id) === String(supId))
    setSupplierName(s?.name ?? po?.supplier_name_snapshot ?? po?.supplier_name ?? null)

    // items bisa dari beberapa key
    const rows =
      (Array.isArray(po?.items) && po.items) ||
      (Array.isArray(data?.purchase_item_side_by_side) && data.purchase_item_side_by_side) ||
      []

    setItems(
      rows.map((r) => ({
        id: r?.id,
        product_unit_id: r?.product_unit_id,
        product_label: r?.product_label ?? r?.product_name ?? r?.product_unit_name ?? r?.product_unit_id,
        uom: r?.uom ?? "PCS",
        qty_ordered: Number(r?.qty_ordered ?? r?.qty ?? 0),
        qty_received:Number(r?.qty_received ?? r?.qty ?? 0),
        cost_price: Number(r?.cost_price ?? r?.price ?? 0),
        sell_price_ref: Number(r?.sell_price_ref ?? r?.sell_price ?? 0),
      }))
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const debouncedSetProductSearch = useMemo(
    () =>
      _.debounce((val) => {
        setProductPage(1)
        setProductSearch(val)
      }, 400),
    []
  )

  useEffect(() => {
    return () => debouncedSetProductSearch.cancel()
  }, [debouncedSetProductSearch])

  const onProductSearchChange = (val) => {
    setProductSearchValue(val)
    debouncedSetProductSearch(val)
  }

  const upperStatus = String(status || "").toUpperCase()

  const isEditable = ["DRAFT", "REJECTED"].includes(upperStatus)
  const isLocked = !isEditable
  const isProductPickBlocked = !outletId || !supplierId
  const productSelectPlaceholder = !outletId
    ? "-- Select Outlet First --"
    : !supplierId
      ? "-- Select Supplier First --"
      : "-- Search & Select Product Unit --"

  // reset product search when outlet or supplier changes
  useEffect(() => {
    debouncedSetProductSearch.cancel()

    setSelectedProductUnitId("")
    setSelectedProductUnit(null)
    setProductSearchValue("")
    setProductSearch("")
    setProductPage(1)
    setProductSelectKey((k) => k + 1)

    productCacheRef.current = new Map()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletId, supplierId])

  // ✅ ps_list params (only if outlet and supplier picked)
  const psListParams = useMemo(() => {
    if (!outletId || !supplierId) return null

    return {
      outlet_group_id: outletGroupId,
      outlet_id: outletId,
      supplier_id: supplierId,

      paginate: true,
      page: productPage,
      per_page: perPage,

      search: productSearch,
    }
  }, [outletId, supplierId, outletGroupId, productSearch, productPage])

  // ✅ prevent request if outlet or supplier not picked
  const rsBaseUrl = psListParams ? process.env.REACT_APP_INVENTORY_API_URL : null

  const { data: productUnitSelection, isLoading: isLoadingProducts } = useResultSet(
    rsBaseUrl,
    "ps_list",
    psListParams || {}
  )

  const productUnitRows = useMemo(() => {
    return Array.isArray(productUnitSelection?.data) ? productUnitSelection.data : []
  }, [productUnitSelection])

  useEffect(() => {
    for (const p of productUnitRows) {
      const key = String(p?.product_unit_id ?? "")
      if (key) productCacheRef.current.set(key, p)
    }
  }, [productUnitRows])

  const productMap = useMemo(() => {
    const map = new Map()
    for (const p of productUnitRows) {
      const key = String(p?.product_unit_id ?? "")
      if (key) map.set(key, p)
    }
    return map
  }, [productUnitRows])

  const totals = useMemo(() => {
    const sub = items.reduce((acc, r) => acc + num(r.qty_ordered) * num(r.cost_price), 0)
    return {
      qtyTotal: items.reduce((acc, r) => acc + num(r.qty_ordered), 0),
      amountTotal: sub,
      sellPriceTotal: items.reduce((acc, r) => acc + num(r.qty_ordered) * num(r.sell_price_ref), 0),
    }
  }, [items])

  const validate = () => {
    const e = {}

    if (!outletId) e.outlet_id = "Outlet wajib dipilih"
    if (!supplierId) e.supplier_id = "Supplier wajib dipilih"
    if (!poDate) e.po_date = "Tanggal PO wajib diisi"
    if (!items.length) e.items = "Minimal 1 item"

    for (const [idx, it] of items.entries()) {
      if (!it.product_unit_id) e[`items.${idx}.product_unit_id`] = "Product wajib"
      if (num(it.qty_ordered) <= 0) e[`items.${idx}.qty_ordered`] = "Qty harus > 0"
      // if (num(it.cost_price) <= 0) e[`items.${idx}.cost_price`] = "Cost price harus > 0"
      if (!it.uom) e[`items.${idx}.uom`] = "UOM wajib"
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  const buildPayload = (status) => ({
    outlet_id: Number(outletId),
    supplier_id: supplierId,
    po_date: poDate,
    po_type: PO_TYPE,
    remarks: notes || null,
    status:status,
    updatedBy: isEdit ? user?.name : null,
    items: items.map((it) => ({
      product_unit_id: it.product_unit_id,
      uom: it.uom,
      qty_ordered: num(it.qty_ordered),
      qty_received: num(it.qty_received),
      cost_price: num(it.cost_price),
      sell_price_ref: num(it.sell_price_ref),
    })),
  })

  const handleSubmitLocal = (status) => {
    if (!validate()) return
    onSubmit?.(buildPayload(status))
  }

  const onAddItem = () => {
    if (!outletId) {
      toast.error("Pilih outlet dulu")
      return
    }

    if (!supplierId) {
      toast.error("Pilih supplier dulu")
      return
    }

    if (!selectedProductUnitId) {
      toast.error("Pilih product dulu")
      return
    }

    const pid = String(selectedProductUnitId)

    const p =
      selectedProductUnit ||
      productCacheRef.current.get(pid) ||
      productMap.get(pid) ||
      productUnitRows.find((x) => String(x?.product_unit_id) === pid)

    if (!p) {
      toast.error("Product tidak ditemukan (list berubah). Pilih ulang ya.")
      return
    }

    const product_unit_id = String(p?.product_unit_id ?? "")
    if (!product_unit_id) {
      toast.error("Product ID tidak valid")
      return
    }

    const existsIndex = items.findIndex((x) => String(x.product_unit_id) === product_unit_id)
    const uom = pickUom(p)
    const cost = pickCostPrice(p)

    if (existsIndex >= 0) {
      const next = [...items]
      next[existsIndex] = {
        ...next[existsIndex],
        qty_ordered: num(next[existsIndex].qty_ordered) + num(addQty || 1),
        uom: next[existsIndex].uom || uom,
      }
      setItems(next)
      toast.success("Qty item ditambahkan")
    } else {
      setItems([
        ...items,
        {
          product_unit_id,
          product_label: pickProductLabel(p),
          uom,
          qty_ordered: num(addQty || 1),
          cost_price: cost,
          price_active_date: p?.price_active_date ?? p?.active_date ?? null,
          sell_price_ref: num(p?.sell_price_ref ?? 0),
        },
      ])
      toast.success("Item ditambahkan")
    }

    debouncedSetProductSearch.cancel()
    setSelectedProductUnitId("")
    setSelectedProductUnit(null)
    setAddQty(1)
    setProductSearchValue("")
    setProductSearch("")
    setProductPage(1)
    setProductSelectKey((k) => k + 1)
  }

  const onRemoveItem = (product_unit_id) => {
    setItems(items.filter((x) => String(x.product_unit_id) !== String(product_unit_id)))
  }

  const onUpdateItem = (product_unit_id, patch) => {
    setItems((prev) =>
      prev.map((it) =>
        String(it.product_unit_id) === String(product_unit_id) ? { ...it, ...patch } : it
      )
    )
  }

  const onApprove = async () => {
    // catatan: approve/cancel ini butuh endpoint PO inventory.
    // kalau di parent kamu yang handle, pindahin ini ke parent juga.
    if (!poId && !id) {
      toast.error("Simpan PO dulu sebelum approve")
      return
    }
    if (!window.confirm(`Approve PO ${poNumber || ""}?`)) return
    try {
      nProgress.start()
      const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
      const url = `${base}/purchase-orders/${poId || id}/approve`
      console.log(user)
      const res = await fetch(url, { method: "POST",body:JSON.stringify({ approvedBy:user?.name }), headers: { "Content-Type": "application/json" } })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || "Gagal approve")
      toast.success(json?.message || "PO berhasil di-approve")
      setStatus("APPROVED")
    } catch (e) {
      toast.error(e.message || "Gagal approve")
    } finally {
      nProgress.done()
    }
  }

  const onCancel = async () => {
    if (!poId && !id) return
    const reason = window.prompt("Alasan cancel (optional):", "")
    if (reason === null) return
    try {
      nProgress.start()
      const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
      const url = `${base}/purchase-orders/${poId || id}/cancel`
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: reason ? JSON.stringify({ reason,cancelledBy:user?.name }) : null,
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || "Gagal cancel")
      toast.success(json?.message || "PO berhasil dibatalkan")
      setStatus("CANCELLED")
    } catch (e) {
      toast.error(e.message || "Gagal cancel")
    } finally {
      nProgress.done()
    }
  }

  const onReject = async () => {
    if (!poId && !id) {
      toast.error("Simpan PO dulu sebelum reject")
      return
    }

    const reason = window.prompt("Alasan reject (wajib diisi):", "")
    if (reason === null) return

    if (!reason.trim()) {
      toast.error("Alasan reject wajib diisi")
      return
    }

    if (!window.confirm(`Reject PO ${poNumber || ""}?`)) return

    try {
      nProgress.start()

      const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
      const url = `${base}/purchase-orders/${poId || id}/reject`

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason,rejectedBy:user?.name }),
      })

      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || "Gagal reject")

      toast.success(json?.message || "PO berhasil di-reject")
      setStatus("REJECTED")
    } catch (e) {
      toast.error(e.message || "Gagal reject")
    } finally {
      nProgress.done()
    }
  }

  const onClose = async () => {
    if (!poId && !id) {
      toast.error("PO tidak ditemukan")
      return
    }

    if (!window.confirm(`Close PO ${poNumber || ""}?`)) return

    try {
      nProgress.start()

      const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
      const url = `${base}/purchase-orders/${poId || id}/close`

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          closedBy: user?.name,
        }),
      })

      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || "Gagal close")

      toast.success(json?.message || "PO berhasil di-close")
      setStatus("CLOSED")
    } catch (e) {
      toast.error(e.message || "Gagal close")
    } finally {
      nProgress.done()
    }
  }

  const canClose =
  isEdit &&
  can?.("purchase-order.close") &&
  ["PARTIAL_RECEIVED", "RECEIVED"].includes(upperStatus)

  const canExport = isEdit && upperStatus === "APPROVED"

  const canCancel = (po) => {
    const st = String(po?.status || "").toUpperCase()
    return !["CANCELLED", "RECEIVED", "PARTIAL_RECEIVED"].includes(st)
  }

  return (
    <div className="pb-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">
            {isEdit ? "Edit Purchase Order" : "Create Purchase Order"}{" "}
            <span className="text-xs font-medium text-neutral-500">({ data?.po_type || PO_TYPE})</span>
          </h1>
          <p className="text-xs text-neutral-500">
            Konsep baru PO: header + items, approve/cancel, lalu goods receipt.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {canExport && (
            <PurchaseExportActions poId={poId || id} />
          )}

          <Link
            to="/purchase"
            className="px-4 py-3 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl hover:bg-neutral-50"
            state={{ back: title, from: location?.pathname, transition: "slide" }}
          >
            Back
          </Link>

          {can?.("purchase-order.cancel") && isEdit && status !== "CANCELLED" && status !== "REJECTED" && status !== 'CLOSED' && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-3 text-xs font-semibold text-white bg-red-600 rounded-xl hover:bg-red-500"
            >
              Cancel
            </button>
          )}

          {can?.("purchase-order.reject") &&
            isEdit &&
            String(status).toUpperCase() === "SUBMITTED" && (
              <button
                type="button"
                onClick={onReject}
                className="px-4 py-3 text-xs font-semibold text-white bg-orange-600 rounded-xl hover:bg-orange-500"
              >
                Reject
              </button>
          )}

          {can?.("purchase-order.approve") && isEdit && String(status).toUpperCase() === "SUBMITTED" && (
            <button
              type="button"
              onClick={onApprove}
              className="px-4 py-3 text-xs font-semibold text-white bg-green-600 rounded-xl hover:bg-green-500"
            >
              Approve
            </button>
          )}

          {canClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-3 text-xs font-semibold text-white bg-purple-600 rounded-xl hover:bg-purple-500"
            >
              Close
            </button>
          )}


          {(can?.("purchase-order.update")) && (
            <button
              type="button"
              onClick={(e) => handleSubmitLocal('DRAFT')}
              disabled={isLocked && isEdit}
              className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                isLocked && isEdit ? "bg-neutral-400 cursor-not-allowed" : "bg-neutral-900 hover:bg-neutral-800"
              }`}
            >
              Save Draft
            </button>
          )}

          {can?.("purchase-order.create") &&
            ["DRAFT", "REJECTED"].includes(String(status).toUpperCase()) && (
            <button
              type="button"
              onClick={(e) => handleSubmitLocal('SUBMITTED')}
              className="px-4 py-3 text-xs font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-500"
            >
              Submit
            </button>
          )}
        </div>
      </div>

      {/* Main Form */}
      <div className="border border-neutral-200 rounded-xl">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-neutral-700">{lang.purchase_number}</label>
              <Input disabled value={poNumber || "Generated by system"} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">{lang.status}</label>
              <Input disabled value={status || "DRAFT"} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">
                {lang.outlet_name}<sup className="text-xs text-red-700"> *</sup>
              </label>
              <SelectSearchable
                disabled={isEdit && isLocked}
                filter={(value) => value.name}
                onChange={(value) => {
                  const b = branchSelection?.find((x) => String(x.id) === String(value))
                  setOutletId(value)
                  setOutletName(b?.name ?? null)
                }}
                selection={branchSelection}
                isLoading={false}
                value={outletId}
                keyValue={(value) => value.id}
                title={(value) => value.name}
                placeholder={`-- Select Outlet --`}
              />
              <ErrorMessage error={errors.outlet_id} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">
                {lang.supplier_name}<sup className="text-xs text-red-700"> *</sup>
              </label>
              <SelectSearchable
                disabled={isEdit && isLocked}
                filter={(value) => value.name}
                onChange={(value) => {
                  setSupplierId(value)
                  const s = supplierSelection?.find((x) => String(x.id) === String(value))
                  setSupplierName(s?.name ?? null)
                }}
                selection={supplierSelection}
                isLoading={false}
                value={supplierId}
                keyValue={(value) => value.id}
                title={(value) => value.name}
                placeholder={`-- Select Supplier --`}
              />
              <ErrorMessage error={errors.supplier_id} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">
                PO Date<sup className="text-xs text-red-700"> *</sup>
              </label>
              <input
                type="date"
                disabled={isEdit && isLocked}
                value={poDate}
                onChange={(e) => setPoDate(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
              <ErrorMessage error={errors.po_date} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">PO Type</label>
              <Input disabled value={data?.po_type || PO_TYPE} />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">{lang.notes}</label>
              <textarea
                disabled={isEdit && isLocked}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
              <ErrorMessage error={errors.notes} />
            </div>
          </div>
        </div>
      </div>

      {/* Add Item */}
      <div className="border border-neutral-200 rounded-xl">
        <div className="px-4 py-5 sm:p-6 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-neutral-900">Items</div>
              <div className="text-xs text-neutral-500">
                Product units.
                Qty Total: <b className="text-lg text-orange-500">{totals.qtyTotal}</b>
              </div>
            </div>
            <div>
            </div>
            <div className="text-xs text-neutral-600">
              HPP Total Amount: <b>{moneyFmt(totals.amountTotal)}</b>
            <div className="text-xs text-neutral-600">
              Sell Price Total Amount: <b>{moneyFmt(totals.sellPriceTotal)}</b>
            </div>
            </div>
          </div>
          <ErrorMessage error={errors.items} />

          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-8">
              <label className="block text-xs text-neutral-700">Product</label>

              <SelectSearchable
                key={`po-product-select-${productSelectKey}`}
                disabled={(isEdit && isLocked) || isProductPickBlocked}
                selection={productUnitRows}
                isLoading={isLoadingProducts}
                value={selectedProductUnitId}
                onChange={(value, row) => {
                  const pid = String(value ?? "")
                  setSelectedProductUnitId(pid)

                  const found =
                    row ||
                    productUnitRows.find((p) => String(p?.product_unit_id) === pid) ||
                    productCacheRef.current.get(pid) ||
                    null

                  if (pid && found) {
                    productCacheRef.current.set(pid, found)
                  }

                  setSelectedProductUnit(found)
                }}
                keyValue={(value) => String(value?.product_unit_id ?? "")}
                title={(value) =>
                  value?.product_unit_long_name ||
                  value?.product_unit ||
                  value?.sku ||
                  value?.product_model ||
                  value?.product_unit_id ||
                  "-"
                }
                description={(value) => {
                  const parts = [
                    value?.product_model,
                    value?.sku ? `SKU: ${value.sku}` : null,
                    value?.product_series,
                    value?.product_brand,
                    value?.product_type,
                  ].filter(Boolean)
                  return parts.join(" • ")
                }}
                placeholder={productSelectPlaceholder}
                reverse
                serverSearch
                searchValue={productSearchValue}
                onSearchChange={onProductSearchChange}
                page={productPage}
                onNextPage={() => {
                  if (productUnitSelection?.meta?.next_page_url) setProductPage((p) => p + 1)
                }}
                onPrevPage={() => {
                  if (productUnitSelection?.meta?.prev_page_url) setProductPage((p) => Math.max(1, p - 1))
                }}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">Qty</label>
              <input
                type="number"
                min={1}
                disabled={isEdit && isLocked}
                value={addQty}
                onChange={(e) => setAddQty(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
            </div>

            <div className="col-span-2">
              <button
                type="button"
                disabled={(isEdit && isLocked) || isProductPickBlocked}
                onClick={onAddItem}
                className={`w-full inline-flex justify-center items-center gap-2 px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                  (isEdit && isLocked) || isProductPickBlocked ? "bg-neutral-400 cursor-not-allowed" : "bg-neutral-900 hover:bg-neutral-800"
                }`}
              >
                <Plus className="w-4 h-4" stroke={1.5} />
                Add
              </button>
            </div>
          </div>

          <div className="overflow-x-auto border border-neutral-200 rounded-xl">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Product</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">UOM</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Qty</th>
                  {status === 'CLOSED' && data?.po_type === 'REGULAR' && <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Qty Received</th>}
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">HPP</th>
                  <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">HPP Total</th>
                  {status === 'CLOSED' && data?.po_type === 'CONSIGNMENT' && <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Sell Price</th>}
                  {status === 'CLOSED' && data?.po_type === 'CONSIGNMENT' && <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Total Sell Price</th>}
                  {status !== 'CLOSED' && <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500">Action</th>}
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-neutral-200">
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-xs text-center text-neutral-500">
                      Belum ada item
                    </td>
                  </tr>
                )}

                {items.map((it, idx) => {
                  const lineTotal = num(it.qty_ordered) * num(it.cost_price)
                  return (
                    <tr key={it.product_unit_id}>
                      <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                        {it.product_label || it.product_unit_id}
                        <div className="text-[11px] text-neutral-500">{it.product_unit_id}</div>

                        {errors[`items.${idx}.product_unit_id`] && (
                          <div className="mt-1 text-[11px] text-red-600">{errors[`items.${idx}.product_unit_id`]}</div>
                        )}
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        <input
                          value={it.uom}
                          disabled={isEdit && isLocked}
                          onChange={(e) => onUpdateItem(it.product_unit_id, { uom: e.target.value })}
                          className="w-24 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                        />
                        {errors[`items.${idx}.uom`] && (
                          <div className="mt-1 text-[11px] text-red-600">{errors[`items.${idx}.uom`]}</div>
                        )}
                      </td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        <input
                          type="number"
                          min={1}
                          value={it.qty_ordered}
                          disabled={isEdit && isLocked}
                          onChange={(e) => onUpdateItem(it.product_unit_id, { qty_ordered: e.target.value })}
                          className="w-24 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                        />
                        {errors[`items.${idx}.qty_ordered`] && (
                          <div className="mt-1 text-[11px] text-red-600">{errors[`items.${idx}.qty_ordered`]}</div>
                        )}
                      </td>

                      {status === 'CLOSED' && data?.po_type === 'REGULAR' && <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        <input
                          type="number"
                          min={1}
                          value={it.qty_received}
                          disabled={true}
                          className="w-24 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                        />
                      </td>}

                      {status !== 'CLOSED' ? <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                        <input
                          type="number"
                          min={0}
                          disabled
                          value={it.cost_price}
                          className="w-36 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                        />
                        <div className="mt-1 text-[11px] text-neutral-500">
                          Active price{it.price_active_date ? `: ${it.price_active_date}` : ""}
                        </div>
                        {errors[`items.${idx}.cost_price`] && (
                          <div className="mt-1 text-[11px] text-red-600">{errors[`items.${idx}.cost_price`]}</div>
                        )}
                      </td> : <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">{moneyFmt(it.cost_price)}</td>}

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">{moneyFmt(lineTotal)}</td>

                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">{moneyFmt(it.sell_price_ref)}</td>
                      <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">{moneyFmt(it.sell_price_ref * it.qty_ordered)}</td>

                      {status !== 'CLOSED' && <td className="px-3 py-4 text-xs text-right whitespace-nowrap">
                        <button
                          type="button"
                          disabled={isEdit && isLocked}
                          onClick={() => onRemoveItem(it.product_unit_id)}
                          className={`px-3 py-2 text-[11px] font-semibold text-white rounded-lg ${
                            isEdit && isLocked ? "bg-neutral-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-500"
                          }`}
                        >
                          Remove
                        </button>
                      </td>}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
